import { ColumnDetails } from '../../../grid/types/common';
import { CategoriesConfig } from '../../../grid/genericGridSmartViews/types';
import { SelectOption } from '../../../ui/gzSelect/types';
import { Feature, Rights } from '@nirvana/shared/constants';

export enum Type {
  CONTAINMENT = 1,
  MITIGATION = 2,
  HARDENING = 3,
  INVESTIGATION = 4,
}

export enum Action {
  ISOLATE_ENDPOINT = 1,
  RESTORE_ENDPOINT = 2,
  PATCH_INSTALL = 3,
  ADD_TO_QUARANTINE = 4,
  RESTORE_FROM_QUARANTINE = 5,
  DELETE = 6,
  DISINFECT = 7,
  KILL_PROCESS = 8,
  ADD_TO_SANDBOX = 9,
  COLLECT_INVESTIGATION_PACKAGE = 10,
  SCAN = 11,
  RISK_SCAN = 12,
}

export enum Target {
  ENDPOINT = 1,
  FILE = 2,
  PROCESS = 3,
  REGISTRY_KEY = 4,
  REGISTRY_VALUE = 5,
}

export enum Source {
  MANUAL = 1,
  CUSTOM_RULE = 2,
  ENDPOINT_PROTECTION = 3,
}

export enum Status {
  PENDING = 0,
  IN_EXECUTION = 1,
  EXECUTION_SUCCESS = 2,
  EXECUTION_FAILED = 3,
  REBOOT_NEEDED = 4,
  ACTION_NEEDED = 5,
  DISMISSED = 6,
  DONE_EXTERNALLY = 7,
}

export const StatusTransitions = {
  [Status.PENDING]: [
    Status.IN_EXECUTION,
    Status.EXECUTION_SUCCESS,
    Status.EXECUTION_FAILED,
    Status.REBOOT_NEEDED,
    Status.ACTION_NEEDED,
  ],
  [Status.IN_EXECUTION]: [
    Status.EXECUTION_SUCCESS,
    Status.EXECUTION_FAILED,
    Status.REBOOT_NEEDED,
    Status.ACTION_NEEDED,
  ],
  [Status.EXECUTION_FAILED]: [Status.PENDING, Status.DISMISSED, Status.DONE_EXTERNALLY],
  [Status.REBOOT_NEEDED]: [Status.DISMISSED, Status.DONE_EXTERNALLY],
  [Status.ACTION_NEEDED]: [Status.DISMISSED, Status.DONE_EXTERNALLY],
};

export interface AddToQuarantineExecutionDetails {
  endpointName: string;
}

export interface AddToSandboxExecutionDetails {
  reportId: string;
}

export enum ErrorId {
  TIMEOUT = 100,
  TASK_ERROR = 200,
  TASK_TIMEOUT = 201,
  CUSTOM_RULE_ERROR = 300,
  ENDPOINT_PROTECTION_ERROR = 400,
  ENDPOINT_PROTECTION_NOT_PERMITTED = 401,
  ENDPOINT_PROTECTION_CORE = 402,
  UNKNOWN = 999,
}

export interface ResponseAction {
  _id: string;
  endpointId: string;
  companyId: string;
  type: Action;
  groupType: Type;
  targetType: Target;
  objectName: string;
  source: Source;
  sourceDetails?: {
    id?: string;
    name: string;
  };
  status: Status;
  executionDetails?: AddToQuarantineExecutionDetails & AddToSandboxExecutionDetails;
  errorId?: ErrorId;
  errorDescription?: string | null;
  executedOn: string;
}

export interface Dependencies {
  navigateToQuarantineItem: ((endpointName: string, file: string, companyId: string | undefined) => void) | undefined;
  isGZ(): boolean;
  isPartnerCompany(): boolean;
  getActiveCompany(): { companyId: string; companyName: string };
  userHasFeature(feature: Feature): boolean;
  userHasAnyRights(rights: Rights[]): boolean;
  userHasAllRights(rights: Rights[]): boolean;
  isSecureLogin(): boolean;
  openEndpointDetailsGeneralWindow(endpointId: string): void;
  showSandboxReport(reportTokenId: string): void;
  openEndpointDetailsInvestigationWindow(endpointId: string): void;
  openEndpointDetailsScanLogsWindow(endpointId: string): void;
}

export enum Events {
  UPDATE_STATUS = 'updateStatus',
  OPEN_CUSTOM_RULE_DETAILS_PANEL = 'openCustomRuleDetailsPanel'
}

export enum Column {
  TYPE,
  ACTION,
  OBJECT,
  TARGET,
  DETAILS,
  STATUS,
  UPDATE_STATUS_ACTIONS,
  EXECUTED_ON,
  EXECUTED_BY,
  SOURCE,
}

export enum ColumnKeys {
  TYPE = 'groupType',
  ACTION = 'type',
  OBJECT = 'objectName',
  TARGET = 'targetType',
  DETAILS = 'details',
  STATUS = 'status',
  UPDATE_STATUS_ACTIONS = 'updateStatusActions',
  EXECUTED_ON = 'executedOn',
  EXECUTED_BY = 'sourceDetails.name',
  SOURCE = 'source',
}

export interface ResponseGridViewData {
  incidentId: string;
  smartViewsCategories: CategoriesConfig;
  allColumns: Array<ColumnDetails<Column, ColumnKeys>>;
  predefinedFilterKeys: Array<ColumnKeys>;
  allUpdateStatusActions: Array<SelectOption>;
}

export const DEFAULT_PAGE_NUMBER = 1;
export const DEFAULT_PAGE_SIZE = 100;

export interface ResponseGridResponse {
  data: Array<ResponseAction>;
  total: number;
}

export interface ResponseGridGetRowsResponse {
  rawEvents: Array<ResponseAction>;
}

export interface CustomRuleDetails {
  rule: any;
  networkTargets: string[];
}
